import type { ColumnDef, RowData } from '@tanstack/react-table';
import type { CSSProperties, ReactNode } from 'react';

import { useCallback, useMemo, useState } from 'react';

import { clsx } from '~/styles/utils';

import type { AppTableFeatures } from './Table.hooks';
import type { ColumnMetaStyles } from './Table.types';

import { TableGridContext } from './TableGrid.context';
import { isLoadingStyle, overflowContainerStyle, tableGridStyle } from './TableGrid.css';
import { useTableContext, useTableScrollTarget } from './TableRoot.context';

const filterHasStyles = <T extends ColumnDef<AppTableFeatures, RowData>>(
  columnDef: T,
): columnDef is T & { meta: { styles: ColumnMetaStyles } } => !!columnDef.meta?.styles;

export type Props = {
  children: ReactNode;
  isLoading?: boolean;
};

export const TableGrid = ({ children, isLoading = false }: Props) => {
  const table = useTableContext();
  const scrollTargetRef = useTableScrollTarget();

  const columnStyles = useMemo(
    () =>
      table
        .getVisibleLeafColumns()
        .map(({ columnDef }) => columnDef)
        .filter(filterHasStyles)
        .map(({ meta }) => meta.styles),
    [table],
  );
  const initialGridTemplateColumns = useMemo(
    () => columnStyles.map((styles) => (styles.minSize ? `minmax(${styles.minSize ?? 0}px, ${styles.size})` : styles.size)),
    [columnStyles],
  );
  const [gridTemplateColumns, setGridTemplateColumns] = useState(initialGridTemplateColumns);
  const resetGridTemplateColumns = useCallback(() => setGridTemplateColumns(initialGridTemplateColumns), [initialGridTemplateColumns]);

  const context = useMemo(
    () => ({ columnStyles, setGridTemplateColumns, resetGridTemplateColumns, isLoading }),
    [columnStyles, isLoading, resetGridTemplateColumns],
  );

  return (
    <TableGridContext value={context}>
      <div className={clsx(overflowContainerStyle, isLoading && isLoadingStyle)} ref={scrollTargetRef}>
        <div
          className={tableGridStyle}
          role="table"
          style={
            {
              '--grid-template-columns': gridTemplateColumns.join(' '),
            } as CSSProperties
          }
        >
          {children}
        </div>
      </div>
    </TableGridContext>
  );
};
